import type { Task } from '../../../types';

export type TaskChip = 'all' | 'overdue' | 'today' | 'week' | 'mine' | 'done';
export type TaskBucket = 'overdue' | 'today' | 'week' | 'later' | 'done';

export const BUCKET_ORDER: TaskBucket[] = ['overdue', 'today', 'week', 'later', 'done'];

export function startOfDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

export function parseTaskDueDate(dueDate: string | undefined | null): Date | null {
  if (!dueDate) return null;
  const dateOnly = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dueDate);
  if (dateOnly) {
    return startOfDay(new Date(Number(dateOnly[1]), Number(dateOnly[2]) - 1, Number(dateOnly[3])));
  }
  const d = new Date(dueDate);
  if (Number.isNaN(d.getTime())) return null;
  return startOfDay(d);
}

export function getTaskDueBucket(task: Task, now: Date = new Date()): TaskBucket {
  if (task.isCompleted) return 'done';
  const today = startOfDay(now);
  const due = parseTaskDueDate(task.dueDate);
  if (!due) return 'later';
  if (due.getTime() < today.getTime()) return 'overdue';
  if (due.getTime() === today.getTime()) return 'today';
  const weekEnd = new Date(today);
  weekEnd.setDate(today.getDate() + 7);
  if (due.getTime() <= weekEnd.getTime()) return 'week';
  return 'later';
}

/** Filtra pelo vencimento no período do datepicker. Sem início = sem filtro. */
export function taskDueInRange(
  dueDate: string | undefined | null,
  start: Date | null,
  end: Date | null,
): boolean {
  if (!start && !end) return true;
  const due = parseTaskDueDate(dueDate);
  if (!due) return false;
  const t = due.getTime();
  if (start && t < startOfDay(start).getTime()) return false;
  if (end && t > startOfDay(end).getTime()) return false;
  return true;
}

export function filterTasksByChip(
  tasks: Task[],
  chip: TaskChip,
  currentUserId?: string | null,
  now: Date = new Date(),
): Task[] {
  if (chip === 'all') return tasks;
  if (chip === 'mine') {
    return tasks.filter(t => !t.isCompleted && t.assignedTo?.id && t.assignedTo.id === currentUserId);
  }
  if (chip === 'done') return tasks.filter(t => t.isCompleted);
  return tasks.filter(t => getTaskDueBucket(t, now) === chip);
}

export function computeTaskKpis(tasks: Task[], now: Date = new Date()) {
  let overdue = 0, today = 0, week = 0, done = 0, pending = 0;
  for (const t of tasks) {
    const b = getTaskDueBucket(t, now);
    if (b === 'overdue') overdue++;
    else if (b === 'today') today++;
    else if (b === 'week') week++;
    else if (b === 'done') done++;
    if (!t.isCompleted) pending++;
  }
  return { overdue, today, week, done, pending };
}

export function groupTasksByBucket(tasks: Task[], now: Date = new Date()) {
  const map = new Map<TaskBucket, Task[]>();
  for (const b of BUCKET_ORDER) map.set(b, []);
  for (const t of tasks) {
    const b = getTaskDueBucket(t, now);
    map.get(b)!.push(t);
  }
  return BUCKET_ORDER
    .map(bucket => ({ bucket, tasks: map.get(bucket)! }))
    .filter(g => g.tasks.length > 0);
}

export function formatTaskDate(iso: string): string {
  const d = parseTaskDueDate(iso);
  if (!d) return '—';
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  return `${dd}/${mm}/${d.getFullYear()}`;
}
